<template>
	<div class="product-table-row" @click="rowClickHandler">
        <div class="product-info-container">
            <div class="chart-container" :class="{ 'hide': showAccounts }">
                <div class="percentage-label">{{product.percentage !== 0 && product.parsedPercentage === 0 ? '≈ ' : ''}}{{product.parsedPercentage}}%</div>
                <chart-simple
                :chartId="chartIdConstructor"
                :graphData="graphData"
                :colorsArray="colorsArray"
                :pieHole="0.8"/>
            </div>
            <div class="product-info">
                <div class="product-name">{{product.description}}</div>
                <div class="product-amount" :class="{ 'hide': showAccounts }">
                    <component class="icon" :class="{'negative': product.totalAmount < 0}" :is="amountIcon(product.totalAmount)"/>
                    <span>{{amountFormat(Math.abs(product.totalAmount), currencyParser(currency))}}</span>
                </div>
            </div>
        </div>
        <icon-arrow-right class="go-to-details-icon" />
	</div>
</template>

<script setup>
import ChartSimple from '@/components/generic/charts/Simple'
import IconArrowRight from '@/assets/icons/arrow-right-full.svg?inline'
/* Vue & Assets */
import { ref, computed, defineAsyncComponent } from 'vue'
import { amountFormat, currencyParser } from '@/assets/scripts/amounts'
import { PATRIMONY_COLORS, PATRIMONY_COLORS_DARK } from '@/assets/constants/core'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits(['mobileRowClick'])

/** props received
@currency {string} - selected currency in our picker
@product {Object} - selected product
@productsList {Array} - list of products
@accountFinancialType {String} - type of financial family account
@savedBackState {Object} - saved back state
*/
const props = defineProps({
    index: Number,
    currency: String,
    product: Object,
    productsList: Array,
    accountFinancialType: String,
    savedBackState: Object
})

const { getAppThemeMode } = mapGetters()

const amountIcon = amount => {
	if (!amount || amount === 0) return null
	const icon = amount > 0 ? 'plus' : 'minus'
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}

const graphData = ref([])
const colorsArray = ref([])
const notSelectedColor = ref('#ebf1f3')
const showAccounts = ref(false)

const graphDataConstructor = () => {
    if (!props.product.totalAmount) {
        graphData.value = [['noAmount', 100]]
        colorsArray.value = [notSelectedColor.value]
    }
    props.productsList.forEach((product, i) => {
        graphData.value.push([
            product.id.replace(/ /g, ''),
            parseInt(product.percentage < 1 && product.percentage !== 0 ? 1 : product.percentage)
        ])
        colorsArray.value.push(product.description === props.product.description ? getAppThemeMode.value === 'dark' ? PATRIMONY_COLORS_DARK[i] : PATRIMONY_COLORS[i] : notSelectedColor.value)
    })
}

const chartIdConstructor = computed(() => {
    return `${props.currency}${props.accountFinancialType}${props.product.id.replace(/ /g, '')}Chart`
})

const rowClickHandler = () => {
    emit('mobileRowClick', {
        ...props.product,
        currency: props.currency,
        accountFinancialType: props.accountFinancialType,
        headerColor: colorsArray.value[props.index] === notSelectedColor.value ? '#ebf2f6' : colorsArray.value[props.index]
    })
}

graphDataConstructor()
</script>

<style lang="scss" scoped>
.product-table-row {
    width: 100%;
    height: 64px;
    padding: 12px 28px 12px 16px;
    border-bottom: 1px solid var(--secondary-light-2);
    overflow: hidden;

    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    cursor: pointer;

    .product-info-container {
        display: flex;
        align-items: center;
        gap: 16px;

        width: 90%;
        .chart-container {
            width: 40px;
            height: 40px;
            position: relative;
            flex-shrink: 0;

            .percentage-label {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);

                @include H9-Regular;
                color: var(--secondary-dark-2);

                z-index: 1;
            }
        }

        .product-info {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            .product-name {
                @include H6-Medium;
                line-height: 18px;
                color: var(--secondary-dark-2);

                margin-bottom: 2px;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .product-amount {
                display: flex;
                align-items: center;
                gap: 4px;

                @include H6-Regular;
                color: var(--secondary-dark-2);

                span {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                svg.icon {
                    width: 12px;
                    height: 12px;

                    :deep(.change-color-fill) {
                        fill: var(--positive);
                    }

                    &.negative {
                        :deep(.change-color-fill) {
                            fill: var(--negative);
                        }
                    }
                }
            }

        }
    }

    svg.go-to-details-icon {
        flex-shrink: 0;

        width: 16px;
        height: 16px;

        :deep(.change-color-fill) {
            fill: var(--secondary-dark-2);
        }
    }
}
</style>
